/**
 * ChatFileUpload — multi-file upload supporting images (max 3) and PDF (max 1).
 * Images are compressed client-side. PDFs are text-extracted client-side.
 */

import { useRef, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Paperclip, X, FileText } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ChatImage } from '@/lib/api'
import { useChatFileIngest, UploadHosted } from './useChatFileIngest'
import { CreativeLightbox } from './CreativeLightbox'
import { CreativeMedia, isVideoCreativeLike } from './CreativeMedia'

interface ChatFileUploadProps {
  images: Array<ChatImage>
  onImagesChange: (images: Array<ChatImage>) => void
  /** Merge-safe emit path for the internal ingest (see useChatFileIngest.onImagesMerge).
   *  QS passes its state setter so a video's late hosted-ref emit can never clobber or
   *  resurrect composer tiles; omit for the legacy plain-array behavior. */
  onImagesMerge?: (updater: (prev: Array<ChatImage>) => Array<ChatImage>) => void
  pdfText: string | null
  pdfName: string | null
  onPdfChange: (text: string | null, name: string | null) => void
  maxImages?: number
  maxSizeMB?: number
  disabled?: boolean
  compact?: boolean
  buttonClassName?: string
  /** Render PDFs to page images instead of extracting text (see useChatFileIngest). On by default. */
  pdfAsImages?: boolean
  /** Cap how many pages a single PDF expands into (see useChatFileIngest). Default unbounded. */
  pdfMaxPagesPerFile?: number
  /** Accept video creatives (Quick Study only — see useChatFileIngest). Default false. */
  acceptVideo?: boolean
  /**
   * Tiles being ingested OUTSIDE this component (e.g. paste/drop wired in the
   * parent's own useChatFileIngest). Added to this component's own button-path
   * pending count so a skeleton shows for every ingest path. Only one path is
   * ever active at a time, so summing is safe.
   */
  pendingCount?: number
  /**
   * Quick Study creative hosting (externalize-creatives, Step C) — forwarded to the internal
   * `useChatFileIngest` so a button-picked image is uploaded on ingest and the emitted
   * `ChatImage` carries `{url,objectKey}`. Omit for Conversations and every other consumer:
   * the button path then behaves byte-for-byte as before (base64-only).
   */
  uploadHosted?: UploadHosted
  /**
   * Prefix for data-testid values. Default `chat-file-upload`. Thread stimulus Swap
   * passes a distinct prefix so two mounts do not share the same ids.
   */
  testIdPrefix?: string
  /**
   * Called only from the tile X/remove control. Parent array replacement
   * (send, seed, merge) must not go through this path.
   */
  onRemoveTile?: (image: ChatImage) => void
}

export function ChatFileUpload({
  images,
  onImagesChange,
  onImagesMerge,
  pdfText,
  pdfName,
  onPdfChange,
  maxImages = 3,
  maxSizeMB = 10,
  disabled = false,
  compact = false,
  buttonClassName,
  pdfAsImages = true,
  pdfMaxPagesPerFile,
  acceptVideo = false,
  pendingCount: externalPendingCount = 0,
  uploadHosted,
  testIdPrefix = 'chat-file-upload',
  onRemoveTile,
}: ChatFileUploadProps) {
  const { t } = useTranslation('app')
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Click-to-expand: a tiny thumbnail opens the full-size creative in a lightbox
  // that steps across every attached creative, matching the thread + report surfaces.
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  // When PDFs become images, the upload is "full" purely on the image budget.
  const isFull = pdfAsImages ? images.length >= maxImages : images.length >= maxImages && pdfText !== null

  const { ingestFiles, pendingCount: buttonPendingCount, pdfDisplayName, clearPdfDisplayName } = useChatFileIngest({ images, onImagesChange, onImagesMerge, onPdfChange, maxImages, maxSizeMB, pdfAsImages, pdfMaxPagesPerFile, uploadHosted, acceptVideo })

  // Skeletons bridge the ~0.5–2s compression/render gap between picking a file
  // and the real thumbnail appearing, so the upload visibly registers.
  const skeletonCount = buttonPendingCount + externalPendingCount

  // Per-tile srcs are memoized: building `data:…;base64,${base64}` in the render map
  // re-materializes a multi-MB string for EVERY tile on EVERY parent render (each
  // keystroke in the composer). Keyed on the stable per-image identity (objectKey when
  // hosted, else a length+head+tail sample of the base64 — length ALONE collides for two
  // distinct same-byte-length creatives, which reused a stale memoized src; sampling stays
  // O(1) per tile, never a multi-MB string join in the render path).
  // A hosted VIDEO prefers its url outright — streaming beats a giant inline data: URL.
  const b64Identity = (b64: string) => `${b64.length}:${b64.slice(0, 32)}:${b64.slice(-32)}`
  const tileKey = images.map((im) => im.objectKey ?? b64Identity(im.base64)).join('|')
  const tileSrcs = useMemo(
    () =>
      images.map((im) =>
        isVideoCreativeLike(im) && im.url ? im.url : `data:${im.mimeType};base64,${im.base64}`,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tileKey is the stable identity of `images`
    [tileKey],
  )

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    await ingestFiles(Array.from(e.target.files ?? []))
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [ingestFiles])

  const removeImage = useCallback((index: number) => {
    const removed = images[index]
    if (removed) {
      onRemoveTile?.(removed)
    }
    onImagesChange(images.filter((_, i) => i !== index))
  }, [images, onImagesChange, onRemoveTile])

  const removePdf = useCallback(() => {
    clearPdfDisplayName()
    onPdfChange(null, null)
  }, [clearPdfDisplayName, onPdfChange])

  const handleButtonClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const hasAttachments = images.length > 0 || pdfText !== null

  return (
    <>
    <div className="flex items-center gap-2">
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptVideo ? 'image/*,video/mp4,video/webm,video/quicktime,application/pdf' : 'image/*,.pdf'}
        multiple
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
        data-testid={`${testIdPrefix}-input`}
      />

      {images.map((img, index) => {
        return (
          <div
            key={index}
            className="relative group"
            data-testid={
              img.registerFailed
                ? `${testIdPrefix}-register-failed-${index}`
                : undefined
            }
          >
            <button
              type="button"
              onClick={() => setLightboxIndex(index)}
              className="block overflow-hidden rounded-lg transition-opacity hover:opacity-90"
              aria-label={t('chatChrome.expandUpload', { index: index + 1 })}
              data-testid={`${testIdPrefix}-preview-${index}`}
            >
              <CreativeMedia
                src={tileSrcs[index]}
                mimeType={img.mimeType}
                alt={t('chatChrome.uploadAlt', { index: index + 1 })}
                className={cn(
                  'h-10 w-10 rounded-lg object-cover border border-border',
                  img.registerFailed && 'ring-2 ring-destructive',
                )}
              />
            </button>
            <button
              onClick={() => removeImage(index)}
              disabled={disabled}
              className="absolute -top-1.5 -right-1.5 rounded-full bg-destructive text-destructive-foreground p-0.5 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
              type="button"
              aria-label={t('chatChrome.removeUpload', { index: index + 1 })}
              data-testid={`${testIdPrefix}-remove-${index}`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )
      })}

      {Array.from({ length: skeletonCount }).map((_, index) => (
        <div
          key={`skeleton-${index}`}
          data-testid={`${testIdPrefix}-skeleton-${index}`}
          aria-hidden="true"
          className="h-10 w-10 animate-pulse rounded-lg border border-border bg-muted"
        />
      ))}

      {pdfName && (
        <div className="relative group inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground">
          <FileText className="h-3 w-3 shrink-0" />
          <span className="max-w-[120px] truncate">{pdfDisplayName ?? pdfName}</span>
          <button
            onClick={removePdf}
            disabled={disabled}
            className="ml-0.5 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/20"
            type="button"
          >
            <X className="h-2.5 w-2.5" />
          </button>
        </div>
      )}

      {!isFull && (
        <button
          type="button"
          onClick={handleButtonClick}
          disabled={disabled}
          data-testid={`${testIdPrefix}-button`}
          aria-label={compact ? t('chatChrome.attach') : hasAttachments ? t('chatChrome.addMore') : t('chatChrome.attach')}
          className={buttonClassName ?? cn(
            'inline-flex items-center rounded-full font-medium transition-colors',
            'text-muted-foreground hover:text-foreground hover:bg-muted/50',
            'disabled:pointer-events-none disabled:opacity-50',
            compact
              ? 'h-7 w-7 justify-center gap-0 px-0 text-xs md:h-auto md:w-auto md:justify-start md:gap-1 md:px-2.5 md:py-1'
              : 'gap-1 px-3.5 py-1.5 text-sm',
          )}
        >
          <Paperclip className={cn('h-3.5 w-3.5', !compact && 'h-4 w-4')} strokeWidth={2} />
          <span className={compact ? 'hidden md:inline' : undefined}>
            {compact ? t('chatChrome.attach') : hasAttachments ? t('chatChrome.addMore') : t('chatChrome.attach')}
          </span>
        </button>
      )}
    </div>
    {lightboxIndex !== null && (
      <CreativeLightbox
        creatives={images.map((im, i) => ({ src: tileSrcs[i], mimeType: im.mimeType }))}
        index={lightboxIndex}
        onIndex={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />
    )}
    </>
  )
}
